import { DeliveredArticle } from '../pages/workingSchedule/types/DeliveredArticle';
import { ScheduledTask } from '../types/scheduling/SchedulingTypes';
import { IntervalWorkTask } from '../types/templateWorkTask';
import { Order } from './Order';
import { ScheduleStartReference } from '../enums/scheduleStartReference';

/** Week-number parities of a weekly interval: "Jämna veckor" and "Ojämna veckor". */
export const WEEK_PARITIES = ['even', 'odd'] as const;

export type WeekParity = (typeof WEEK_PARITIES)[number];

/**
 * Interval model representing a recurring schedule pattern for an order.
 *
 * Defines how often and when work should be performed for a specific order.
 * Can be weekly, monthly, daily, or callOff (on-demand).
 *
 * @property id - Optional unique identifier for the interval
 * @property orderId - ID of the associated order
 * @property order - Complete order object associated with this interval
 * @property startDate - Start date of the interval in YYYY-MM-DD format
 * @property endDate - End date of the interval in YYYY-MM-DD format, or null if ongoing
 * @property frequency - Frequency multiplier (e.g., '1' for every week, '2' for every other week). Empty when weekParity is set
 * @property weekParity - Weekly intervals only: run in even or odd ISO weeks instead of every N weeks
 * @property intervalType - Type of interval: 'weekly', 'monthly', 'daily', or 'callOff'
 * @property occasions - Array of occasions (time slots) within this interval
 */
export interface Interval {
  id?: string;
  orderId: string;
  order: Order;
  startDate: string;
  endDate: string | null;
  frequency: string;
  weekParity?: WeekParity | null;
  intervalType: 'weekly' | 'monthly' | 'daily' | 'callOff';
  occasions: IntervalOccasion[];
  discount: string;
  discountType: string;
  priceAfterDiscount: string;
  sumOfYieldOfPeriod: string;
  numberOfOccasions: string;
  invoiceType: string;
  debitType: string;
  description: string;
  deliveredProducts: DeliveredArticle[];
  globalTransportCostDiscount: string;
  globalTransportCostDiscountType: string;
  globalTransportCostPriceAfterDiscount: string;
  globalTransportCostListPrice?: string;
  serviceListPrice?: string;
}

/**
 * What a preferred-resource row in an interval occasion means.
 *
 * `None` is an explicit "no preferred resource for this period" marker, used between or
 * after real preferred employees. It participates in the same date-range chaining, so a
 * period covered by a `None` row is deliberately left for manual planning instead of
 * falling through to a later row.
 *
 * A row without `kind` predates this field and is read as `Employee`.
 */
export enum PreferredEmployeeKind {
  Employee = 'employee',
  None = 'none',
}

/**
 * Preferred employee assignment for an interval occasion.
 *
 * Specifies which employee should be preferred for a specific occasion
 * within a date range.
 *
 * @property employeeId - The preferred employee's id (Employee.id, a uuid - not the employee
 *   number), or null when kind is `None`
 * @property startDate - Start date of the preference period in YYYY-MM-DD format
 * @property endDate - End date of the preference period in YYYY-MM-DD format, or null if ongoing
 * @property kind - Row kind; absent on rows stored before the kind field existed, read as `Employee`
 */
export interface PreferredEmployee {
  employeeId: string | null;
  startDate: string;
  endDate: string | null;
  kind?: PreferredEmployeeKind;
}

/**
 * Occasion model representing a time slot within an interval.
 *
 * Defines the work details, timing, and tasks for a specific occurrence
 * within an interval. An interval can have multiple occasions.
 *
 * @property id - Optional unique identifier for the occasion
 * @property workMinutes - Length of the work in minutes
 * @property transportMinutes - Length of the transport in minutes
 * @property type - Type of occasion: 'flexible' (has end time) or 'fixed' (no end time)
 * @property scheduleStartReference - Whether the transport is performed inside the
 *   shift ('transportStart') or before the start time ('shiftStart')
 * @property workTasks - Array of work tasks to be performed in this occasion
 * @property intervalId - Optional ID of the parent interval
 * @property events - Array of events (specific time slots) for this occasion
 * @property preferredEmployees - Optional array of preferred employees for this occasion
 */

export enum IntervalOccasionPerWeekday {
  Monday = 'monday',
  Tuesday = 'tuesday',
  Wednesday = 'wednesday',
  Thursday = 'thursday',
  Friday = 'friday',
  Saturday = 'saturday',
  Sunday = 'sunday',
}

export const IntervalOccasionPerWeekday_Translation = {
  [IntervalOccasionPerWeekday.Monday]: 'Måndag',
  [IntervalOccasionPerWeekday.Tuesday]: 'Tisdag',
  [IntervalOccasionPerWeekday.Wednesday]: 'Onsdag',
  [IntervalOccasionPerWeekday.Thursday]: 'Torsdag',
  [IntervalOccasionPerWeekday.Friday]: 'Fredag',
  [IntervalOccasionPerWeekday.Saturday]: 'Lördag',
  [IntervalOccasionPerWeekday.Sunday]: 'Söndag',
};

export enum IntervalOccasionPerWeekDayFrequencyUnit {
  First = 'first',
  Second = 'second',
  Third = 'third',
  Fourth = 'fourth',
  Last = 'last',
}

export const IntervalOccasionPerWeekDayFrequencyUnit_Translation = {
  [IntervalOccasionPerWeekDayFrequencyUnit.First]: 'Första',
  [IntervalOccasionPerWeekDayFrequencyUnit.Second]: 'Andra',
  [IntervalOccasionPerWeekDayFrequencyUnit.Third]: 'Tredje',
  [IntervalOccasionPerWeekDayFrequencyUnit.Fourth]: 'Fjärde',
  [IntervalOccasionPerWeekDayFrequencyUnit.Last]: 'Sista',
};

export enum IntervalOccasionPerType {
  Date = 'date',
  Weekday = 'weekday',
}

export interface IntervalOccasion {
  id?: string;
  workMinutes: number | null;
  transportMinutes: number | null;
  numberOfUnits: string;
  type: 'flexible' | 'fixed';
  scheduleStartReference: ScheduleStartReference;
  workTasks: IntervalWorkTask[];
  intervalId?: string;
  events: IntervalEvent[];
  preferredEmployees?: PreferredEmployee[];
  perType?: IntervalOccasionPerType | null | undefined;
  perDate?: string | null | undefined;
  perWeekday?: IntervalOccasionPerWeekday | null | undefined;
  perWeekDayFrequencyUnit?: IntervalOccasionPerWeekDayFrequencyUnit | null | undefined;
  numberOfUnitsAtUnitPrice?: string;
  transportMinutesAtUnitPrice?: number | null;
  transportCostAtUnitPrice?: string;
  parkingCostAtUnitPrice?: string;
  scheduledTasks: ScheduledTask[];
  minutesPerUnit?: number | null;
}

/**
 * Event model representing a specific time slot within an occasion.
 *
 * Defines when work should occur on a specific day and time.
 * Used for occasions that have multiple scheduled times.
 *
 * @property id - Optional unique identifier for the event
 * @property day - Day of the week (e.g., 'monday', 'tuesday') or null for flexible scheduling
 * @property startTime - Start time in HH:mm format (e.g., '09:00')
 * @property endTime - Optional end time in HH:mm format (required for flexible type occasions)
 */
export interface IntervalEvent {
  id?: string;
  day: string | null;
  startTime: string;
  endTime?: string;
}
